import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ChartPie, DoorOpen } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  AnalyticsAreaFillDefs,
  analyticsAreaFillUrl,
  useAnalyticsChartInstanceId,
} from "../../shared/chartAreaFill";
import { formatAnalyticsCurrency } from "../../shared/analyticsFormatting";
import { MetricCard, MetricCardsGrid } from "../../shared";
import { AnalyticsDonutChartArea } from "../../shared/AnalyticsDonutChartArea";
import { RevenueChartCard } from "../../revenue/RevenueChartCard";
import { RevenueChartLegend } from "../../revenue/RevenueChartLegend";
import { REVENUE_CHART_CARD_ICON_STROKE_WIDTH } from "../../revenue/constants";
import {
  SERVICES_CHART_CARD_ICON_COLOR,
  SERVICES_CONTENT_GAP,
  SERVICES_CONTENT_PADDING,
} from "../../services/constants";
import { translateOffersLabel } from "../translateOffersLabel";
import type { OffersKpi, RevenueTrendPoint, UpgradeSegment } from "../types";

interface RoomUpgradeOfferWidgetProps {
  metrics: OffersKpi[];
  usageSegments: UpgradeSegment[];
  revenueTrend: RevenueTrendPoint[];
  onExportTrend?: (format: "csv" | "xlsx") => void;
  exportTrendDisabled?: boolean;
  onExportUsage?: (format: "csv" | "xlsx") => void;
  exportUsageDisabled?: boolean;
}

function UpgradeUsageCard({
  segments,
  onExport,
  exportDisabled,
}: {
  segments: UpgradeSegment[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const chartConfig = useMemo(
    () =>
      segments.reduce<ChartConfig>((config, segment) => {
        config[segment.key] = {
          label: segment.name,
          color: segment.fill,
        };
        return config;
      }, {}),
    [segments],
  );

  const total = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.value, 0),
    [segments],
  );
  const totalShareLabel = t(
    "analytics.offers.roomUpgrade.totalShare",
    "Total share",
  );

  return (
    <RevenueChartCard
      title={t("analytics.offers.roomUpgrade.upgradeUsage", "Upgrade Usage")}
      icon={<ChartPie />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      className="flex h-full min-h-0 w-full flex-col"
      showExport={Boolean(onExport)}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center justify-between gap-3">
        <AnalyticsDonutChartArea
          segments={segments}
          total={total}
          chartConfig={chartConfig}
          centerPrimary={`${total}%`}
          centerSecondary={totalShareLabel}
        />
        <ul className="flex w-full min-w-0 flex-col gap-2">
          {segments.map((segment) => (
            <li
              key={segment.key}
              className="flex items-center justify-between gap-4"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: segment.fill }}
                />
                <span className="truncate text-sm leading-normal text-[#171717]">
                  {segment.name}
                </span>
              </div>
              <span className="shrink-0 text-sm text-[#737373]">
                {segment.value}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </RevenueChartCard>
  );
}

function RevenueTrendCard({
  data,
  onExport,
  exportDisabled,
}: {
  data: RevenueTrendPoint[];
  onExport?: (format: "csv" | "xlsx") => void;
  exportDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const revenueTrendChartConfig = useMemo(
    () =>
      ({
        revenue: {
          label: t("analytics.offers.roomUpgrade.revenue", "Revenue"),
          color: "#826BFF",
        },
      }) satisfies ChartConfig,
    [t],
  );
  const revenueLabel = t("analytics.offers.roomUpgrade.revenue", "Revenue");
  const legendItems = useMemo(
    () => [{ key: "revenue", label: revenueLabel, color: "#826BFF" }],
    [revenueLabel],
  );
  const maxValue =
    data.length > 0 ? Math.max(...data.map((item) => item.revenue)) : 0;
  const yAxisMax =
    maxValue <= 0 ? 100 : Math.max(100, Math.ceil(maxValue / 1000) * 1000);
  const chartInstanceId = useAnalyticsChartInstanceId();

  const xAxisInterval =
    data.length > 14 ? Math.max(1, Math.floor(data.length / 7)) : 0;

  return (
    <RevenueChartCard
      title={t("analytics.offers.roomUpgrade.revenueTrend", "Revenue Trend")}
      icon={<ArrowUpRight />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      className="flex h-full min-h-0 w-full flex-col"
      showExport={Boolean(onExport)}
      onExport={onExport}
      exportDisabled={exportDisabled}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <ChartContainer
          config={revenueTrendChartConfig}
          className="flex !aspect-auto min-h-0 w-full flex-1 flex-col items-stretch justify-start [&_.recharts-responsive-container]:!h-full [&_.recharts-wrapper]:!h-full"
        >
          <AreaChart
            data={data}
            margin={{ top: 8, right: 8, left: 0, bottom: 4 }}
          >
            <defs>
              <AnalyticsAreaFillDefs
                seriesKeys={["revenue"]}
                chartInstanceId={chartInstanceId}
              />
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              height={32}
              interval={xAxisInterval}
              className="text-muted-foreground text-xs"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={54}
              domain={[0, yAxisMax]}
              tickFormatter={(value) =>
                formatAnalyticsCurrency(Number(value), {
                  maximumFractionDigits: 0,
                  symbol: "€",
                  symbolPosition: "suffix",
                })
              }
              className="text-muted-foreground text-xs"
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="dot"
                  formatter={(value) => [
                    formatAnalyticsCurrency(Number(value), {
                      maximumFractionDigits: 0,
                      symbol: "€",
                      symbolPosition: "suffix",
                    }),
                    revenueLabel,
                  ]}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="var(--color-revenue)"
              strokeWidth={2}
              fill={analyticsAreaFillUrl("revenue", chartInstanceId)}
            />
          </AreaChart>
        </ChartContainer>
        <RevenueChartLegend items={legendItems} />
      </div>
    </RevenueChartCard>
  );
}

export const RoomUpgradeOfferWidget: React.FC<RoomUpgradeOfferWidgetProps> = ({
  metrics,
  usageSegments,
  revenueTrend,
  onExportTrend,
  exportTrendDisabled = false,
  onExportUsage,
  exportUsageDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="flex w-full flex-col rounded-2xl border border-[#E5E5E5] bg-white"
      style={{ padding: SERVICES_CONTENT_PADDING, gap: SERVICES_CONTENT_GAP }}
    >
      <div className="flex items-center gap-3">
        <DoorOpen
          className="size-4 shrink-0"
          style={{ color: SERVICES_CHART_CARD_ICON_COLOR }}
          strokeWidth={REVENUE_CHART_CARD_ICON_STROKE_WIDTH}
          aria-hidden
        />
        <h2 className="text-sm font-semibold text-[#171717]">
          {t("analytics.offers.roomUpgrade.title", "Room Upgrade Offer")}
        </h2>
      </div>

      <MetricCardsGrid columns={4}>
        {metrics.map((metric) => (
          <MetricCard
            key={metric.title}
            title={translateOffersLabel(t, metric.title)}
            value={metric.value}
            delta={metric.delta}
            trend={metric.trend}
            icon={metric.icon}
            width="fill"
          />
        ))}
      </MetricCardsGrid>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
        <UpgradeUsageCard
          segments={usageSegments}
          onExport={onExportUsage}
          exportDisabled={exportUsageDisabled}
        />
        <RevenueTrendCard
          data={revenueTrend}
          onExport={onExportTrend}
          exportDisabled={exportTrendDisabled}
        />
      </div>
    </div>
  );
};
